import clsx from "clsx"
import { forwardRef, useEffect, useRef, useState } from "react"

import { MS_PER_SECOND } from "@constants/time"

// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import DocumentDownload from "../DocumentDownload/DocumentDownload"
import styles from "./PDFViewer.module.scss"
import { PDFViewerProps } from "./PDFViewer.types"

const PDF_LOAD_TIMEOUT_MS = 3 * MS_PER_SECOND
const PDF_MIMETYPE = "application/pdf"

function PDFViewer(props: PDFViewerProps, ref: React.Ref<HTMLDivElement>) {
    const { src, filename, className } = props

    const [hasError, setHasError] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const resolvedRef = useRef(false)

    useEffect(() => {
        resolvedRef.current = false
        setHasError(false)
        setIsLoading(true)

        const abortController = new AbortController()

        timeoutRef.current = setTimeout(() => {
            if (!resolvedRef.current) {
                resolvedRef.current = true
                abortController.abort()
                setHasError(true)
                setIsLoading(false)
            }
        }, PDF_LOAD_TIMEOUT_MS)

        fetch(src, { method: "HEAD", signal: abortController.signal })
            .then((response) => {
                if (resolvedRef.current || abortController.signal.aborted) {
                    return
                }
                resolvedRef.current = true
                setHasError(!response.ok)
                setIsLoading(false)
            })
            .catch(() => {
                if (resolvedRef.current || abortController.signal.aborted) {
                    return
                }
                resolvedRef.current = true
                setHasError(true)
                setIsLoading(false)
            })
            .finally(() => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current)
                    timeoutRef.current = null
                }
            })

        return () => {
            abortController.abort()
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [src])

    if (isLoading) {
        return null
    } else if (hasError) {
        return <DocumentDownload src={src} filename={filename} mimeType={PDF_MIMETYPE} className={className} />
    } else {
        return (
            <div ref={ref} className={clsx(styles.container, className)}>
                <iframe src={src} title={filename} className={styles.iframe} />
            </div>
        )
    }
}

export default forwardRef(PDFViewer)
